import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useCallback, useState, useMemo, useEffect } from "react";
import { CallApiRePassword, CallApiGetVerifyCode } from "../Api/Enforcement";
import { SwalError, SwalSuccess } from "../Share/Swal";
import { accuracy, login, rePass } from "../Api/SubUrl";
import useCookieStorage from "./useCookieStorage";
import {
  FORGET_PASS_USER_ID_KEY,
  FORGET_PASS_EMAIL_KEY,
} from "../Constants/localStorageKey";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";
import { handleClearStoredAuth, notifyClientAuthChanged } from "../Share/Func/Storage";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
};

export default function useRePasswordPage() {
  const [loading, setLoading] = useState(false);
  const [isSentVerify, setIsSentVerify] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { getValue, removeValue } = useCookieStorage();
  const navigate = useNavigate();

  useEffect(() => {
    if (!getValue(FORGET_PASS_USER_ID_KEY)) {
      navigate(accuracy);
    }
  }, [getValue, navigate]);

  const {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("rePassword", rePass);

  const rePassSchemaForm = yup.object().shape({
    userName: yup.string().required("Vui lòng nhập tên tài khoản !"),
    password: yup
      .string()
      .required("Vui lòng nhập mật khẩu")
      .min(6, "Độ dài mật khẩu từ 6 đến 32 ký tự")
      .max(32, "Độ dài mật khẩu từ 6 đến 32 ký tự"),
    rePassword: yup
      .string()
      .required("Vui lòng xác nhận lại mật khẩu")
      .test(
        "is-same-password",
        "Mật khẩu không trùng khớp!",
        function (rePassword) {
          return rePassword === this.parent.password;
        }
      ),
    verifyCode: yup.string().required("Vui lòng nhập mã xác nhận"),
  });

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        userName: getValue(FORGET_PASS_USER_ID_KEY) || "",
        password: "",
        rePassword: "",
        verifyCode: "",
      }),
      [getValue]
    ),
    resolver: yupResolver(rePassSchemaForm),
  });

  const handleRePass = useCallback(
    async (formData) => {
      if (loading) return;
      try {
        const token = await getReCaptchaToken();
        setLoading(true);

        CallApiRePassword(token, formData, (res) => {
          setLoading(false);
          if (res.Status) {
            resetReCaptcha();
            const status = res.Status.status;
            const message = res.Status.data.Message;
            if (status === 400)
              ShowCustomErrorModal({
                message: `<p>${message}</p>`,
                title: "Đăng nhập thất bại",
              });
            else if (status === 403)
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.RECAPTCHA_INVALID,
                title: "Xác thực thất bại",
              });
            else
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.GENERIC_ERROR,
                title: "Đã xảy ra lỗi",
              });
          } else {
            if (res !== "success") {
              resetReCaptcha();
              ShowCustomErrorModal({
                message:
                  "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
                title: "Đã xảy ra lỗi",
              });
              return;
            }
            reset();
            removeValue(FORGET_PASS_USER_ID_KEY);
            removeValue(FORGET_PASS_EMAIL_KEY);
            SwalSuccess("Cập nhật thành công!");
            navigate(login);
            handleClearStoredAuth();
            notifyClientAuthChanged();
          }
        });
      } catch (error) {
        resetReCaptcha();
        ShowCustomErrorModal({
          message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
          title: "Cập nhật thất bại",
        });
      }
    },
    [getReCaptchaToken, loading, navigate, removeValue, reset, resetReCaptcha]
  );

  const handleGetVerifyCode = useCallback(() => {
    if (isSending) return;
    setIsSending(true);
    const param = {
      userName: getValue(FORGET_PASS_USER_ID_KEY),
      email: getValue(FORGET_PASS_EMAIL_KEY),
    };
    CallApiGetVerifyCode(param, (res) => {
      setIsSending(false);
      if (res.Status) {
        const response = res.Status;
        if (response.status === 400)
          ShowCustomErrorModal({
            message: `<p>${response.data.Message}</p>`,
            title: "Đã xảy ra lỗi",
          });
        else
          SwalError("Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ");
      } else {
        if (res !== "success") {
          SwalError("Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ");
        }
        SwalSuccess("Mã xác nhận đã được gửi!");
        setIsSentVerify(true);
        setTimeout(() => setIsSentVerify(false), 10000);
      }
    });
  }, [getValue, isSending]);

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Cập nhật thất bại",
    });
  }, []);

  return {
    control,
    handleSubmit: handleSubmit(handleRePass, handleInvalid),
    isSentVerify,
    handleGetVerifyCode,
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
    loading,
    isSending,
  };
}
